/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM, T, useTranslate } from "@adapptio/lib-components";
import React, { FunctionComponent, MouseEvent, useCallback, useContext, useMemo } from "react";
import { ResourceService } from "../../resources/ResourceService";
import { EditorMainContext } from "../contexts/EditorMainContext";
import { IResourceListProps, IResourceNode, ResourceList } from "./ResourceList";
import { termsEditorCommon as terms } from "../../terms/common";
import { Button } from "./Button/Button";
import { FC_ANCHOR } from "./FloatingContainer";
import { RESOURCE_TYPES } from "@adapptio/lib-core";
import { normalizeResourcePath } from "../../resources/ResourceHelpers";
import { useEditorState } from "../hooks";

interface IMenuButtonProps {
	id: string;
	icon?: string;
	label: string;
	tooltip?: string;
	isPrimary?: boolean;
	isEnabled?: boolean;
	onClick: () => void;
}

export const MenuButton: FunctionComponent<IMenuButtonProps> = ({
	id,
	icon,
	label,
	tooltip,
	isPrimary,
	isEnabled,
	onClick
}) => {
	const t = useTranslate();

	const baseClass = BEM("edt-app-menu-button", null, {
		"style-primary": isPrimary
	});

	baseClass.add("m-menu-btn-id-" + id);

	return (
		<Button
			icon={icon}
			label={t("editor", label)}
			tooltip={t("editor", tooltip)}
			baseClass={baseClass}
			disabled={!isEnabled}
			onClick={onClick}
			tooltipAnchor={FC_ANCHOR.RIGHT_CENTER}
		/>
	);

	/*
	return <button
		className={baseClass.toClassName()}
		onClick={onClick}
		disabled={isEnabled === false ? true : false}
		title={tooltip}
	>
		{ icon ? <Icon name={icon} size={ICON_SIZE.SMALL} /> : null }
		<span className={baseClass.element("menu-button-label").toClassName()}>
			<T domain="editor" term={label} />
		</span>
	</button>;
	*/
};

interface IMenuButtonGroupProps {
	menuId: string;
	title: string;
	onInteraction?: (data: { menuId: string; itemId: string }) => void;
}

export const MenuButtonGroup: FunctionComponent<IMenuButtonGroupProps> = ({
	menuId,
	title,
	onInteraction
}) => {
	const ctx = useContext(EditorMainContext);

	const menuItems = useMemo(() => {
		return ctx.menuService.getMenuItems(menuId);
	}, [ menuId ]);

	const handleItemClick = useCallback(
		(itemId: string) => {
			ctx.menuService.dispatchMenuItem(menuId, itemId).catch((err) => {
				console.error("Failed to dispatch menu item:", menuId, itemId, err);
			});

			if (onInteraction) {
				onInteraction({ menuId, itemId });
			}
		},
		[ menuId, ctx.menuService, onInteraction ]
	);

	if (menuItems.length === 0) {
		return null;
	}

	const baseClass = BEM("edt-app-menu", "menu-button-group");

	return (
		<>
			<h2 className={baseClass.element("section-title").toClassName()}>
				<T domain="editor" term={title} />
			</h2>
			<nav className={baseClass.toClassName()}>
				{menuItems.map((item) => {
					const keyCombination = ctx.keyBindingService.getKeysForCommandAsString(
						item.commandId,
						item.commandArgs
					);

					return (
						<MenuButton
							key={item.id}
							id={item.id}
							icon={item.icon}
							label={item.label}
							tooltip={
								item.tooltip || "" + (keyCombination !== "" ? ` (${keyCombination})` : "")
							}
							isPrimary={item.flags.includes("primary")}
							isEnabled={
								(item.isEnabled ? item.isEnabled() : true) &&
								!item.flags.includes("comingSoon")
							}
							onClick={() => handleItemClick(item.id)}
						/>
					);
				})}
			</nav>
		</>
	);
};

interface IMenuLinkGroupProps {
	menuId: string;
	title: string;
	onInteraction?: (data: { menuId: string; itemId: string }) => void;
}

export const MenuLinkGroup: FunctionComponent<IMenuLinkGroupProps> = ({ menuId, title, onInteraction }) => {
	const t = useTranslate();
	const ctx = useContext(EditorMainContext);

	const menuItems = useMemo(() => {
		return ctx.menuService.getMenuItems(menuId);
	}, [ menuId ]);

	const handleItemClick = useCallback(
		(ev: MouseEvent<HTMLAnchorElement>, itemId: string) => {
			ev.preventDefault();

			ctx.menuService.dispatchMenuItem(menuId, itemId).catch((err) => {
				console.error("Failed to dispatch menu item:", menuId, itemId, err);
			});

			if (onInteraction) {
				onInteraction({ menuId, itemId });
			}
		},
		[ menuId, ctx.menuService, onInteraction ]
	);

	if (menuItems.length === 0) {
		return null;
	}

	const baseClass = BEM("edt-app-menu", "menu-links");

	return (
		<>
			<h2 className={baseClass.element("section-title").toClassName()}>
				<T domain="editor" term={title} />
			</h2>
			<nav className={baseClass.toClassName()}>
				{menuItems.map((item) => {
					const keyCombination = ctx.keyBindingService.getKeysForCommandAsString(
						item.commandId,
						item.commandArgs
					);

					return (
						<li key={item.id}>
							<a
								href={`#${item.id}`}
								title={
									(item.tooltip && t("editor", item.tooltip)) ||
									"" + (keyCombination !== "" ? ` (${keyCombination})` : "")
								}
								onClick={(ev) => handleItemClick(ev, item.id)}
							>
								<T domain="editor" term={item.label} />
							</a>
						</li>
					);
				})}
			</nav>
		</>
	);
};

/**
 * Component props
 */
export interface IAppMenuProps {
	resourceService: ResourceService;
	isActive: boolean;
	onInteraction: (data: { node?: IResourceNode; menuId?: string; itemId?: string }) => void;
}

export const APP_MENU_STATE_STORAGE_KEY = "appMenu.state";
export const APP_MENU_STATE_DEFAULT = { nodes: {} };

/**
 * App Menu component
 */
export const AppMenu: FunctionComponent<IAppMenuProps> = ({ resourceService, isActive, onInteraction }) => {
	const ctx = useContext(EditorMainContext);

	const [ treeState, setTreeState ] = useEditorState(APP_MENU_STATE_STORAGE_KEY, { nodes: {} });

	const handleResourceSelect = useCallback(
		(node: IResourceNode) => {
			if (node.resourceType !== RESOURCE_TYPES.DIRECTORY) {
				ctx.commandsService.execute("resource.open", node.id).catch((err) => {
					console.error("Failed to open resource:", node, err);
				});
			}

			if (onInteraction) {
				onInteraction({ node });
			}
		},
		[ resourceService, onInteraction, ctx.commandsService ]
	);

	const handleResourceDragDrop = useCallback<IResourceListProps["onNodeDragDrop"]>(
		({ draggedNode, dropTargetNode, dropTargetParentNode }) => {
			const draggedResource = draggedNode?.metaData?.resourceNode as IResourceNode;
			const dropTargetResource = dropTargetNode?.metaData?.resourceNode as IResourceNode;
			const dropTargetParentResource = dropTargetParentNode?.metaData?.resourceNode as IResourceNode;
			const targetResource =
				dropTargetResource?.resourceType === RESOURCE_TYPES.DIRECTORY
					? dropTargetResource
					: dropTargetParentResource;
			const fullTargetPath = normalizeResourcePath(targetResource?.basePath, targetResource?.name);

			if (draggedResource?.basePath === fullTargetPath) {
				return;
			}

			ctx.commandsService
				.execute("resource.move", draggedResource.id, targetResource.id)
				.catch((err) => {
					console.error("Failed to execute resource move command:", err);
				});
		},
		[]
	);

	const isResourceNodeSelectable = useCallback<IResourceListProps["isSelectable"]>((resourceNode) => {
		return resourceNode.resourceType !== RESOURCE_TYPES.DIRECTORY;
	}, []);

	const baseClass = BEM("edt-app-menu");
	baseClass.add("edt-base");

	if (!isActive) {
		return null;
	}

	return (
		<div className={baseClass.toClassName()}>
			<div className={baseClass.element("column-resource-list").toClassName()}>
				<ResourceList
					isActive={isActive}
					onSelect={handleResourceSelect}
					onNodeDragDrop={handleResourceDragDrop}
					isSelectable={isResourceNodeSelectable}
					treeState={treeState}
					onTreeStateChange={setTreeState}
					showRoot
				/>
			</div>
			<div className={baseClass.element("column-menu").toClassName()}>
				<MenuButtonGroup
					menuId="main.create"
					title={terms.mainMenu.sectionCreate.title}
					onInteraction={onInteraction}
				/>
				<MenuButtonGroup
					menuId="main.manage"
					title={terms.mainMenu.sectionManage.title}
					onInteraction={onInteraction}
				/>
				<MenuButtonGroup
					menuId="main.tools"
					title={terms.mainMenu.sectionTools.title}
					onInteraction={onInteraction}
				/>
				<MenuLinkGroup
					menuId="main.support"
					title={terms.mainMenu.sectionSupport.title}
					onInteraction={onInteraction}
				/>
				<MenuLinkGroup
					menuId="main.inspiration"
					title={terms.mainMenu.sectionInspiration.title}
					onInteraction={onInteraction}
				/>
			</div>
			{/* <div className={baseClass.element("column-links").toClassName()}></div> */}
		</div>
	);
};
